import React, { useState, useEffect } from 'react';

const AccessorySelection = ({ 
  accessories, 
  selectedAccessories, 
  accessoryQuantities,
  transportOption,
  selectedPoles = [],
  onToggle, 
  onQuantityChange,
  onTransportSelect,
  onNext, 
  onPrevious 
}) => {
  const [accessoryCategories, setAccessoryCategories] = useState({
    transport_options: [],
    compatible_cables: [],
    optional_accessories: []
  });
  const [poleInfo, setPoleInfo] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const heightNum = (h) => parseFloat(String(h).replace(/[^\d.]/g, '')) || 0;
  const tallestPole = selectedPoles.reduce(
    (max, sp) => (!max || heightNum(sp.pole.height) > heightNum(max.pole.height) ? sp : max),
    null
  )?.pole;
  const tallestPoleId = tallestPole?.id;

  useEffect(() => {
    const fetchAccessoryCategories = async () => {
      try {
        if (tallestPoleId) {
          // Use smart compatibility API (basierend auf höchstem Mast)
          const response = await fetch(`${BACKEND_URL}/api/accessories/compatible/${tallestPoleId}`);
          const data = await response.json();
          setAccessoryCategories({
            transport_options: data.transport_options,
            compatible_cables: data.compatible_cables,
            optional_accessories: data.optional_accessories
          });
          setPoleInfo(data.pole_info);
        } else {
          // Fallback to categories API
          const response = await fetch(`${BACKEND_URL}/api/accessories/by-categories`);
          const data = await response.json();
          setAccessoryCategories({
            transport_options: data.transport_options,
            compatible_cables: [],
            optional_accessories: data.optional_accessories
          });
        }
      } catch (error) {
        console.error('Fehler beim Laden der Zubehör-Kategorien:', error);
      }
    };

    fetchAccessoryCategories();
  }, [BACKEND_URL, tallestPoleId]);

  // Kombiniere kompatible Kabel mit anderen optionalen Zubehörteilen
  const compatibleAccessories = [
    ...accessoryCategories.compatible_cables,
    ...accessoryCategories.optional_accessories.filter(accessory => {
      if (selectedPoles.length === 0) return false;
      // Mounting-Kompatibilität (Erdstück / Fußplatte / all)
      const mountingOk = accessory.compatibility.includes('all') ||
             selectedPoles.some(sp => accessory.compatibility.includes(sp.pole.mounting));
      // Optionale Durchmesser-Kompatibilität (z. B. Mastbox 165 vs. 250)
      const diameterOk = !accessory.diameter_compatibility ||
             accessory.diameter_compatibility.length === 0 ||
             selectedPoles.some(sp => accessory.diameter_compatibility.includes(sp.pole.diameter));
      return mountingOk && diameterOk;
    })
  ];

  const isSelected = (accessoryId) => {
    return selectedAccessories.some(acc => acc.id === accessoryId);
  };

  // XF-Anker ist bei Masten mit Fußplatte Pflicht
  const ANKER_ID = 'xf_anker';
  const isFussplatte = selectedPoles.some(sp => sp.pole.mounting === 'fußplatte');
  const fussplattenCount = selectedPoles
    .filter(sp => sp.pole.mounting === 'fußplatte')
    .reduce((sum, sp) => sum + (sp.quantity || 1), 0);
  const ankerAccessory = compatibleAccessories.find(acc => acc.id === ANKER_ID);

  // Pflicht-Anker automatisch mit Anzahl der Fußplatten-Masten vorbelegen (Menge bleibt anpassbar)
  useEffect(() => {
    if (isFussplatte && ankerAccessory && !isSelected(ANKER_ID)) {
      onToggle(ankerAccessory, fussplattenCount);
    }
  }, [isFussplatte, ankerAccessory, selectedAccessories, fussplattenCount]);

  const getQuantity = (accessoryId) => {
    const q = accessoryQuantities[accessoryId];
    return q === undefined ? 1 : q;
  };

  const handleQuantityChange = (accessory, quantity) => {
    // Direkt die Menge aktualisieren, ohne das Accessory zu entfernen (0 erlaubt)
    onQuantityChange(accessory, quantity);
  };

  const handleToggleAccessory = (accessory) => {
    // Pflicht-Anker bei Fußplatte darf nicht entfernt werden
    if (isFussplatte && accessory.id === ANKER_ID) return;
    if (isSelected(accessory.id)) {
      // Entfernen (quantity = 0)
      onToggle(accessory, 0);
    } else {
      // Hinzufügen mit Standard-Menge 1
      onToggle(accessory, 1);
    }
  };

  const ankerMissing = isFussplatte && ankerAccessory && !isSelected(ANKER_ID);
  const canProceed = transportOption && Object.keys(transportOption).length > 0 && !ankerMissing;

  return (
    <div className="animate-slideIn">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">
          Zubehör auswählen
        </h2>
        <p className="text-slate-100 max-w-2xl mx-auto drop-shadow">
          Ergänzen Sie Ihre Kameramast-Konfiguration mit passendem Zubehör. 
          Alle angezeigten Artikel sind mit Ihrem gewählten Mast kompatibel.
        </p>
      </div>

      {/* Gewählte Masten Übersicht */}
      {selectedPoles.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Ihre gewählten Kameramasten
            <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full" data-testid="selected-poles-total">
              Gesamt: {selectedPoles.reduce((sum, sp) => sum + (sp.quantity || 1), 0)} Stück
            </span>
          </h3>
          <div className="space-y-3">
            {selectedPoles.map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`selected-pole-overview-${idx}`}>
                <div>
                  <h4 className="font-semibold text-slate-900">{entry.pole.name}</h4>
                  <p className="text-sm text-slate-600">
                    {entry.pole.height} • {entry.pole.diameter} • 
                    {entry.pole.mounting === 'erdstück' ? ' Erdstück' : ' Fußplatte'} • 
                    Menge: {entry.quantity}
                  </p>
                </div>
                <span className={`pole-badge ${
                  entry.pole.type === 'starre' ? 'badge-starre' : 'badge-kippbar'
                }`}>
                  {entry.pole.type === 'starre' ? 'Starrer Mast' : 'Kippbarer Mast'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transportkostenpauschale (PFLICHT) */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Transportkostenpauschale
          </h3>
          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            PFLICHT
          </span>
        </div>
        <p className="text-slate-600 mb-4 text-sm">
          Bitte wählen Sie eine Transportoption für die Lieferung Ihres Kameramasts.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accessoryCategories.transport_options.map(transport => (
            <div
              key={transport.id}
              className={`card cursor-pointer transition-all ${
                transportOption?.id === transport.id ? 'card-selected bg-blue-50' : ''
              }`}
              onClick={() => onTransportSelect(transport)}
              data-testid={`transport-option-${transport.id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {transport.name}
                  </h4>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    transportOption?.id === transport.id 
                      ? 'border-blue-600 bg-blue-600' 
                      : 'border-slate-300'
                  }`}>
                    {transportOption?.id === transport.id && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <p className="text-slate-600 text-xs mb-2">
                  {transport.description}
                </p>
                <span className="text-xs text-slate-500">
                  Art.-Nr.: {transport.article_number}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optionales Zubehör */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        {poleInfo && accessoryCategories.compatible_cables.length > 0 && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">
              📏 Intelligente Kabelauswahl für {poleInfo.name}
            </h4>
            <p className="text-blue-700 text-sm">
              Masthöhe (höchster Mast): {poleInfo.height} → Mindeste empfohlene Kabellänge: {poleInfo.min_cable_length}
            </p>
            <p className="text-blue-600 text-xs mt-1">
              Es werden nur Patchkabel mit mindestens dieser Länge angezeigt.
            </p>
          </div>
        )}
        
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Optionales Zubehör
        </h3>
        
        {compatibleAccessories.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {compatibleAccessories.map(accessory => (
            <div
              key={accessory.id}
              className={`accessory-card card ${
                isSelected(accessory.id) ? 'selected card-selected' : ''
              }`}
              data-testid={`accessory-card-${accessory.id}`}
            >
              <div className="p-6">
                {/* Checkbox */}
                <div className={`accessory-checkbox ${
                  isSelected(accessory.id) ? 'checked' : ''
                }`}>
                  {isSelected(accessory.id) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>

                {/* Produktbild oder Icon */}
                {accessory.image_url ? (
                  <div className="accessory-image-container mb-4">
                    <img 
                      src={accessory.image_url} 
                      alt={accessory.name}
                      className="accessory-image"
                      onError={(e) => {
                        console.log('Accessory image failed to load, showing fallback icon');
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        console.log(`Successfully loaded authentic Xortec image for ${accessory.name}`);
                        e.target.nextElementSibling.style.display = 'none';
                      }}
                    />
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                      {accessory.type === 'fundamentanker' ? (
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
                        </svg>
                      )}
                    </div>
                    <div className="image-source-badge" style={{ display: 'none' }}></div>
                    {accessory.id === 'barox_lt_lpite_402gbtme' && (
                      <div
                        className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-[10px] font-semibold rounded-full shadow"
                        data-testid="barox-mastbox-hint"
                      >
                        Für Xortec Mastbox 165mm / 250mm Edelstahl
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mb-4">
                    {accessory.type === 'fundamentanker' ? (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
                      </svg>
                    )}
                  </div>
                )}

                {/* Name & Typ */}
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                  {accessory.name}
                  {isFussplatte && accessory.id === ANKER_ID && (
                    <span
                      className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full"
                      data-testid="anker-pflicht-badge"
                    >
                      notwendiges Zubehör
                    </span>
                  )}
                </h3>
                <div className="text-sm text-blue-600 font-medium mb-3">
                  {accessory.type === 'fundamentanker' ? 'Fundamentanker' : 
                   accessory.type === 'schlüssel' ? 'Schlüssel' : 
                   accessory.type === 'patchkabel' ? 'Industrielles Patchkabel' :
                   accessory.type === 'mastbox' ? 'Mastanschlusskasten' :
                   accessory.type === 'switch' ? 'Netzwerk-Switch' :
                   accessory.type === 'medienkonverter' ? 'Smart City Medienkonverter' : 
                   accessory.type}
                </div>

                {/* Beschreibung mit USPs (max. 3 sichtbar, Rest ausklappbar) */}
                <div className="text-slate-600 text-sm mb-4">
                  {accessory.description.includes('•') ? (
                    <div>
                      {(() => {
                        const lines = accessory.description.split('\n');
                        const bullets = lines.filter(l => l.includes('•'));
                        const others = lines.filter(l => !l.includes('•'));
                        const expanded = !!expandedDescriptions[accessory.id];
                        const visibleBullets = expanded ? bullets : [];
                        return (
                          <>
                            {others.map((line, index) => (
                              line.includes('PREMIUM') || line.includes('FEATURES') ? (
                                <div key={`h-${index}`} className="font-semibold text-blue-700 text-xs mb-2">
                                  {line}
                                </div>
                              ) : (
                                line.trim() ? <p key={`t-${index}`} className="text-xs mb-1">{line}</p> : null
                              )
                            ))}
                            {visibleBullets.map((line, index) => (
                              <div key={`b-${index}`} className="flex items-start mb-1">
                                <span className="text-blue-500 mr-2 mt-0.5">•</span>
                                <span className="text-xs">{line.replace('•', '').trim()}</span>
                              </div>
                            ))}
                            {bullets.length > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedDescriptions(prev => ({ ...prev, [accessory.id]: !expanded }));
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline mt-1"
                                data-testid={`toggle-description-${accessory.id}`}
                              >
                                {expanded ? '− Eigenschaften ausblenden' : `+ Eigenschaften anzeigen (${bullets.length})`}
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <p>{accessory.description}</p>
                  )}
                </div>

                {/* Mengenauswahl */}
                {isSelected(accessory.id) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor={`quantity-${accessory.id}`}>
                      Menge:
                    </label>
                    <input
                      id={`quantity-${accessory.id}`}
                      type="number"
                      min={accessory.id === ANKER_ID ? 0 : 1}
                      step="1"
                      value={getQuantity(accessory.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        const raw = parseInt(e.target.value, 10);
                        const minVal = accessory.id === ANKER_ID ? 0 : 1;
                        const val = Number.isFinite(raw) && raw >= minVal ? raw : minVal;
                        handleQuantityChange(accessory, val);
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid={`quantity-${accessory.id}`}
                    />
                    {isFussplatte && accessory.id === ANKER_ID && (
                      <p className="text-xs text-slate-500 mt-1" data-testid="anker-quantity-hint">
                        Empfehlung: {fussplattenCount} Stück (entspricht Anzahl der Fußplatten-Masten) – wählen Sie 0, falls Sie eigene Anker bereitstellen.
                      </p>
                    )}
                  </div>
                )}

                {/* Auswahl Button */}
                {isFussplatte && accessory.id === ANKER_ID ? (
                  <button
                    className="w-full btn btn-disabled cursor-not-allowed"
                    disabled
                    data-testid={`toggle-accessory-${accessory.id}`}
                  >
                    ✓ Notwendiges Zubehör
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleAccessory(accessory);
                    }}
                    className={`w-full btn ${
                      isSelected(accessory.id) ? 'btn-secondary' : 'btn-primary'
                    }`}
                    data-testid={`toggle-accessory-${accessory.id}`}
                  >
                    {isSelected(accessory.id) ? 'Entfernen' : 'Hinzufügen'}
                  </button>
                )}

                {/* Datenblatt-Links */}
                {accessory.id === 'barox_lt_lpite_402gbtme' && (
                  <a
                    href="https://xortec.de/media/ac/b3/44/1718878256/700270234.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block w-full text-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline mt-3"
                    data-testid="datenblatt-barox-link"
                  >
                    📄 Datenblatt LT-LPITE-402GBTME
                  </a>
                )}
                {accessory.id === 'xortec_mastbox_165' && (
                  <a
                    href="/docs/Xortec_Datenblatt_Mastbox_165mm_Edelstahl.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block w-full text-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline mt-3"
                    data-testid="datenblatt-mastbox-card-link"
                  >
                    📄 Datenblatt Mastbox 165mm Edelstahl
                  </a>
                )}
                {accessory.id === 'xortec_mastbox_250' && (
                  <a
                    href="/docs/Xortec_Datenblatt_Mastbox_250mm_Edelstahl.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block w-full text-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline mt-3"
                    data-testid="datenblatt-mastbox-250-card-link"
                  >
                    📄 Datenblatt Mastbox 250mm Edelstahl
                  </a>
                )}

                {/* Artikel-Nummer und Zusatzinfos */}
                <div className="pt-4 border-t border-slate-200 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">
                      Art.-Nr.: {accessory.article_number}
                    </span>
                    {(accessory.type === 'mastbox' || accessory.type === 'switch') && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {accessory.type === 'mastbox' ? 'Smart City Ready' : 'PoE++ Industrial'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Kein kompatibles Zubehör verfügbar
          </h3>
          <p className="text-slate-600">
            Für den ausgewählten Mast ist aktuell kein passendes Zubehör verfügbar.
          </p>
        </div>
      )}
      </div>


      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onPrevious}
          className="btn btn-secondary"
          data-testid="previous-step-btn"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück
        </button>

        <div className="text-sm text-slate-600">
          {transportOption ? (
            <div className="text-center">
              <span className="text-green-600 font-medium">✓ Transport gewählt</span>
              {selectedAccessories.length > 0 && (
                <div className="mt-1">
                  {selectedAccessories.length} Zubehörteil{selectedAccessories.length === 1 ? '' : 'e'} ausgewählt
                </div>
              )}
            </div>
          ) : (
            <span className="text-red-600 font-medium">⚠ Bitte Transport wählen</span>
          )}
        </div>

        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`btn ${canProceed ? 'btn-primary' : 'btn-disabled'}`}
          data-testid="next-step-btn"
        >
          Weiter
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AccessorySelection;